//src/pages/cadastros/TenantOnboardingPage.tsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle2,
  ChevronRight,
  Download,
  FileSpreadsheet,
  Mail,
  Rocket,
  ShieldCheck,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/shared/PageHeader";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  useCreateTenantOnboarding,
  useDownloadTenantOnboardingOrganizationImportTemplate,
  useSendTenantOnboardingWelcomeEmail,
  useTenantDetail,
  useTenantOnboardingStatus,
  useTenantOnboardingTemplates,
  useValidateTenantOnboardingOrganizationImport,
} from "@/hooks/useTenants";
import type {
  TenantOnboardingOrganizationImportFile,
  TenantOnboardingOrganizationImportValidation,
  TenantOnboardingSummarySection,
  TenantOnboardingTemplateItem,
} from "@/services/tenants";

const STEPS = [
  { shortLabel: "Início", fullLabel: "Estrutura inicial" },
  { shortLabel: "Grupo", fullLabel: "Grupo administrador" },
  { shortLabel: "Usuário", fullLabel: "Usuário administrador" },
  { shortLabel: "Pré-cadastros", fullLabel: "Pré-cadastros Normex" },
  { shortLabel: "Importação", fullLabel: "Importar estrutura e usuários" },
  { shortLabel: "Revisão", fullLabel: "Revisão" },
  { shortLabel: "Conclusão", fullLabel: "Conclusão" },
];

const TEMPLATE_GROUPS: Array<{
  key: keyof TemplateSelection;
  title: string;
  description: string;
  dataKey: TemplateDataKey;
}> = [
  {
    key: "dimension_ids",
    title: "Dimensões",
    description: "Base de dimensões usadas em questionários e indicadores psicossociais.",
    dataKey: "dimensions",
  },
  {
    key: "risk_ids",
    title: "Riscos",
    description: "Catálogo de riscos base do tenant Normex.",
    dataKey: "risks",
  },
  {
    key: "action_plan_model_ids",
    title: "Planos de ação",
    description: "Modelos de planos e ações padrão para uso na gestão dos riscos.",
    dataKey: "action_plan_models",
  },
  {
    key: "group_ids",
    title: "Grupos de acesso",
    description: "Grupos do tenant Normex com cópia somente de permissões de escopo tenant.",
    dataKey: "groups",
  },
  {
    key: "dimension_risk_rule_ids",
    title: "Vínculo Riscos × Dimensões",
    description: "Regras de sugestão de riscos por dimensão. Só serão copiadas se risco e dimensão forem selecionados.",
    dataKey: "dimension_risk_rules",
  },
  {
    key: "risk_action_plan_model_ids",
    title: "Vínculo Planos de Ação × Riscos",
    description: "Associações entre riscos e modelos de planos. Só serão copiadas se risco e plano forem selecionados.",
    dataKey: "risk_action_plan_models",
  },
  {
    key: "questionnaire_ids",
    title: "Questionários",
    description: "Questionários, versões, seções, perguntas, opções e vínculos com dimensões disponíveis.",
    dataKey: "questionnaires",
  },
];

type TemplateDataKey =
  | "dimensions"
  | "risks"
  | "action_plan_models"
  | "groups"
  | "dimension_risk_rules"
  | "risk_action_plan_models"
  | "questionnaires";

type TemplateSelection = {
  dimension_ids: string[];
  risk_ids: string[];
  action_plan_model_ids: string[];
  group_ids: string[];
  dimension_risk_rule_ids: string[];
  risk_action_plan_model_ids: string[];
  questionnaire_ids: string[];
};

type OnboardingResult = {
  admin_user_id: string;
  admin_user_email: string;
  admin_user_name: string;
  temporary_password: string;
  template_summary?: Record<string, TenantOnboardingSummarySection>;
  organization_import_summary?: Record<string, TenantOnboardingSummarySection>;
};

const emptySelection: TemplateSelection = {
  dimension_ids: [],
  risk_ids: [],
  action_plan_model_ids: [],
  group_ids: [],
  dimension_risk_rule_ids: [],
  risk_action_plan_model_ids: [],
  questionnaire_ids: [],
};

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function onlyNameText(value: string) {
  return value.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ'´`^~\s.-]/g, "");
}

function maskBrazilianPhone(value: string) {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 2) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

function validMaskedPhone(value: string) {
  const digits = value.replace(/\D/g, "");
  return digits.length === 0 || digits.length === 10 || digits.length === 11;
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || "").split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

export default function TenantOnboardingPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const tenantId = id;

  const tenantQuery = useTenantDetail(tenantId);
  const statusQuery = useTenantOnboardingStatus(tenantId);
  const templatesQuery = useTenantOnboardingTemplates(tenantId, !!tenantId && statusQuery.isSuccess && !statusQuery.data?.is_read_only && Boolean(statusQuery.data?.can_run_templates ?? true));
  const createMut = useCreateTenantOnboarding(tenantId);
  const sendEmailMut = useSendTenantOnboardingWelcomeEmail(tenantId);
  const validateImportMut = useValidateTenantOnboardingOrganizationImport(tenantId);
  const downloadTemplateMut = useDownloadTenantOnboardingOrganizationImportTemplate();

  const [step, setStep] = useState(0);
  const [unitName, setUnitName] = useState("Matriz");
  const [departmentName, setDepartmentName] = useState("Administrativo");
  const [jobPositionName, setJobPositionName] = useState("Administrador do Sistema");
  const [groupName, setGroupName] = useState("Administrador do Tenant");
  const [adminName, setAdminName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPhone, setAdminPhone] = useState("");
  const [migrateTemplates, setMigrateTemplates] = useState(true);
  const [templateSelection, setTemplateSelection] = useState<TemplateSelection>(emptySelection);
  const [templatesInitialized, setTemplatesInitialized] = useState(false);
  const [importNow, setImportNow] = useState(false);
  const [organizationImport, setOrganizationImport] = useState<TenantOnboardingOrganizationImportFile>({ enabled: false });
  const [importValidation, setImportValidation] = useState<TenantOnboardingOrganizationImportValidation | null>(null);
  const [result, setResult] = useState<OnboardingResult | null>(null);
  const [finishConfirmOpen, setFinishConfirmOpen] = useState(false);

  const tenantName = tenantQuery.data?.person?.name ?? "Tenant";
  const currentStatus = statusQuery.data?.status ?? "pending";
  const isConfigured = Boolean(statusQuery.data?.is_configured || currentStatus === "configured" || result?.status === "configured");
  const isReadOnly = Boolean(statusQuery.data?.is_read_only || isConfigured);
  const canSendWelcomeEmail = Boolean(statusQuery.data?.can_send_welcome_email || isConfigured);
  const canEditOnboarding = !isConfigured && Boolean(statusQuery.data?.can_edit_onboarding ?? true);
  const isBusy = createMut.isPending || sendEmailMut.isPending || validateImportMut.isPending || downloadTemplateMut.isPending;
  const templates = templatesQuery.data;
  const adminUserId = result?.admin_user_id || statusQuery.data?.admin_user_id || null;
  const adminUserEmail = result?.admin_user_email || statusQuery.data?.admin_user_email || adminEmail || null;
  const welcomeAlreadySent = Boolean(statusQuery.data?.welcome_email_sent_at);
  const welcomeEmailActionLabel = welcomeAlreadySent ? "Reenviar e-mail de boas-vindas" : "Enviar e-mail de boas-vindas";
  const welcomeEmailStatusText = welcomeAlreadySent
    ? `Enviado em ${new Date(String(statusQuery.data?.welcome_email_sent_at)).toLocaleString("pt-BR")}`
    : "Pendente";
  const persistedTemplateSummary = result?.template_summary || statusQuery.data?.template_summary || {};
  const persistedImportSummary = result?.organization_import_summary || statusQuery.data?.organization_import_summary || {};
  const onboardingCompletedAt = statusQuery.data?.onboarding_completed_at
    ? new Date(String(statusQuery.data.onboarding_completed_at)).toLocaleString("pt-BR")
    : null;
  const hasImportSummary = Object.values(persistedImportSummary || {}).some(
    (value) => Boolean(value?.created || value?.skipped_existing || value?.skipped_dependency || value?.errors?.length),
  );
  const hasTemplateSummary = Object.values(persistedTemplateSummary || {}).some(
    (value) => Boolean(value?.created || value?.skipped_existing || value?.skipped_dependency || value?.errors?.length),
  );

  useEffect(() => {
    if (isReadOnly || !templates || templatesInitialized) return;
    setTemplateSelection({
      dimension_ids: templates.dimensions.map((item) => item.id),
      risk_ids: templates.risks.map((item) => item.id),
      action_plan_model_ids: templates.action_plan_models.map((item) => item.id),
      group_ids: templates.groups.map((item) => item.id),
      dimension_risk_rule_ids: templates.dimension_risk_rules.map((item) => item.id),
      risk_action_plan_model_ids: templates.risk_action_plan_models.map((item) => item.id),
      questionnaire_ids: templates.questionnaires.map((item) => item.id),
    });
    setTemplatesInitialized(true);
  }, [isReadOnly, templates, templatesInitialized]);

  useEffect(() => {
    if (isReadOnly && !result) {
      setStep(6);
    }
  }, [isReadOnly, result]);

  useEffect(() => {
    if (!statusQuery.data || result) return;
    if (statusQuery.data.unit_name) setUnitName(statusQuery.data.unit_name);
    if (statusQuery.data.department_name) setDepartmentName(statusQuery.data.department_name);
    if (statusQuery.data.job_position_name) setJobPositionName(statusQuery.data.job_position_name);
    if (statusQuery.data.group_name) setGroupName(statusQuery.data.group_name);
    if (statusQuery.data.admin_user_name) setAdminName(statusQuery.data.admin_user_name);
    if (statusQuery.data.admin_user_email) setAdminEmail(statusQuery.data.admin_user_email);
    if (statusQuery.data.admin_user_phone) setAdminPhone(statusQuery.data.admin_user_phone);
  }, [result, statusQuery.data]);

  const canGoNext = useMemo(() => {
    if (step === 0) return unitName.trim() && departmentName.trim() && jobPositionName.trim();
    if (step === 1) return groupName.trim();
    if (step === 2) return adminName.trim() && isValidEmail(adminEmail) && validMaskedPhone(adminPhone);
    if (step === 4) return !importNow || importValidation?.valid;
    return true;
  }, [adminEmail, adminName, adminPhone, departmentName, groupName, importNow, importValidation?.valid, jobPositionName, step, unitName]);

  function goBack() {
    navigate("/cadastros/empresa");
  }

  function nextStep() {
    if (!canGoNext) {
      toast.error("Revise os dados obrigatórios e formatos antes de avançar");
      return;
    }
    setStep((value) => Math.min(value + 1, STEPS.length - 1));
  }

  function prevStep() {
    setStep((value) => Math.max(value - 1, 0));
  }

  function toggleTemplateItem(key: keyof TemplateSelection, id: string, checked: boolean) {
    setTemplateSelection((current) => {
      const values = current[key] || [];
      return {
        ...current,
        [key]: checked ? Array.from(new Set([...values, id])) : values.filter((value) => value !== id),
      };
    });
  }

  function setAllTemplateItems(key: keyof TemplateSelection, items: TenantOnboardingTemplateItem[], checked: boolean) {
    setTemplateSelection((current) => ({
      ...current,
      [key]: checked ? items.map((item) => item.id) : [],
    }));
  }

  async function handleDownloadTemplate() {
    try {
      const response = await downloadTemplateMut.mutateAsync();
      downloadBlob(response.blob, response.filename || "modelo-importacao-estrutura-usuarios.xlsx");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao baixar modelo de importação");
    }
  }

  async function handleImportFile(file?: File | null) {
    setImportValidation(null);
    setOrganizationImport({ enabled: false });
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Envie um arquivo .xlsx");
      return;
    }
    try {
      const contentBase64 = await fileToBase64(file);
      const validation = await validateImportMut.mutateAsync({ filename: file.name, content_base64: contentBase64 });
      setImportValidation(validation);
      setOrganizationImport({ enabled: validation.valid, filename: file.name, content_base64: contentBase64 });
      if (validation.valid) {
        toast.success(`Planilha validada com ${validation.total_rows} registro(s)`);
      } else {
        toast.error("A planilha possui erros de validação");
      }
    } catch (e: any) {
      toast.error(e?.message || "Erro ao validar planilha");
    }
  }

  function validateFinalData() {
    if (!adminName.trim()) return "Informe o nome do usuário administrador";
    if (!isValidEmail(adminEmail)) return "Informe um e-mail válido para o usuário administrador";
    if (!validMaskedPhone(adminPhone)) return "Telefone do administrador inválido";
    if (importNow && !importValidation?.valid) return "Valide a planilha de estrutura e usuários antes de concluir";
    return null;
  }

  function requestFinish() {
    const validationError = validateFinalData();
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setFinishConfirmOpen(true);
  }

  async function handleFinish() {
    if (!tenantId) return;

    try {
      const created = await createMut.mutateAsync({
        structure: {
          unit_name: unitName.trim(),
          department_name: departmentName.trim(),
          job_position_name: jobPositionName.trim(),
        },
        admin_group: {
          name: groupName.trim(),
          permission_codes: [],
        },
        admin_user: {
          name: adminName.trim(),
          email: adminEmail.trim(),
          phone: adminPhone.trim() || null,
          is_active: true,
        },
        template_selection: {
          migrate_templates: migrateTemplates,
          ...templateSelection,
        },
        organization_import: importNow ? organizationImport : { enabled: false },
      });
      setResult(created);
      setStep(6);
      setFinishConfirmOpen(false);
      toast.success("Onboarding concluído");
    } catch (e: any) {
      toast.error(e?.message || "Erro ao concluir onboarding do tenant");
    }
  }

  async function sendWelcomeEmail() {
    if (!canSendWelcomeEmail) {
      toast.error("Conclua o onboarding antes de enviar o e-mail de boas-vindas");
      return;
    }
    if (!adminUserId) {
      toast.error("Usuário administrador não encontrado para envio");
      return;
    }

    try {
      const response = await sendEmailMut.mutateAsync(adminUserId);
      toast.success(response.message || (welcomeAlreadySent ? "E-mail de boas-vindas reenviado" : "E-mail de boas-vindas enviado"));
    } catch (e: any) {
      toast.error(e?.message || "Erro ao enviar e-mail de boas-vindas");
    }
  }

  return (
    <div className="pb-10">
      <PageHeader title="Onboarding inicial do tenant" description={`Configurando tenant: ${tenantName}`}>
        <Button variant="outline" onClick={goBack} disabled={isBusy}>
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar
        </Button>
      </PageHeader>

      <Card className="mb-6 p-4">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Rocket className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Jornada de habilitação</span>
              <Badge variant={isConfigured ? "default" : currentStatus === "in_progress" ? "outline" : "secondary"}>
                {isConfigured ? "Configurado" : currentStatus === "in_progress" ? "Em onboarding" : "Pendente"}
              </Badge>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Configure estrutura mínima, administrador, modelos Normex e importação opcional de estrutura e usuários.
            </p>
          </div>

          <OnboardingBreadcrumb step={step} onSelect={setStep} disabled={isBusy} />
        </div>
      </Card>

      {statusQuery.isLoading || tenantQuery.isLoading ? (
        <Card className="p-6 text-sm text-muted-foreground">Carregando dados do tenant...</Card>
      ) : null}

      {isReadOnly && step !== 6 ? (
        <Card className="mb-6 border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          Este tenant já possui onboarding configurado. Você ainda pode reenviar o e-mail de boas-vindas ao administrador cadastrado.
        </Card>
      ) : null}

      {step === 0 ? (
        <Card className="p-6">
          <h2 className="text-lg font-semibold">Estrutura inicial</h2>
          <p className="mt-1 text-sm text-muted-foreground">Informe a unidade, departamento e cargo iniciais do tenant.</p>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <InputField label="Unidade/Filial" value={unitName} onChange={setUnitName} disabled={isReadOnly || isBusy} />
            <InputField label="Departamento" value={departmentName} onChange={setDepartmentName} disabled={isReadOnly || isBusy} />
            <InputField label="Cargo" value={jobPositionName} onChange={setJobPositionName} disabled={isReadOnly || isBusy} />
          </div>
        </Card>
      ) : null}

      {step === 1 ? (
        <Card className="p-6">
          <h2 className="text-lg font-semibold">Grupo administrador</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            O grupo receberá somente permissões locais do tenant. Permissões Normex/platform nunca serão atribuídas ao cliente.
          </p>

          <div className="mt-6 max-w-lg space-y-2">
            <Label>Nome do grupo</Label>
            <Input value={groupName} onChange={(e) => setGroupName(e.target.value)} disabled={isReadOnly || isBusy} />
          </div>

          <div className="mt-5 flex items-start gap-3 rounded-xl border bg-muted/40 p-4 text-sm text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-4 w-4" />
            <div>
              <div className="font-medium text-foreground">Perfil administrador completo do tenant</div>
              <div>O sistema aplicará o conjunto padrão de permissões locais para o primeiro administrador.</div>
            </div>
          </div>
        </Card>
      ) : null}

      {step === 2 ? (
        <Card className="p-6">
          <h2 className="text-lg font-semibold">Usuário administrador</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Crie o primeiro login administrador do tenant. O primeiro acesso será orientado pelo e-mail de boas-vindas.
          </p>

          <div className="mt-6 grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input value={adminName} onChange={(e) => setAdminName(onlyNameText(e.target.value))} disabled={isReadOnly || isBusy} />
            </div>
            <div className="space-y-2">
              <Label>E-mail</Label>
              <Input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} disabled={isReadOnly || isBusy} />
              {adminEmail && !isValidEmail(adminEmail) ? <p className="text-xs text-red-600">Informe um e-mail válido.</p> : null}
            </div>
            <div className="space-y-2">
              <Label>Telefone</Label>
              <Input value={adminPhone} onChange={(e) => setAdminPhone(maskBrazilianPhone(e.target.value))} disabled={isReadOnly || isBusy} placeholder="(51) 99999-9999" />
              {!validMaskedPhone(adminPhone) ? <p className="text-xs text-red-600">Telefone deve ter DDD + número com 10 ou 11 dígitos.</p> : null}
            </div>
          </div>
        </Card>
      ) : null}

      {step === 3 ? (
        <Card className="p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-lg font-semibold">Pré-cadastros Normex</h2>
              <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
                {isReadOnly
                  ? "Consulte o resultado real dos modelos Normex aplicados durante o onboarding."
                  : "Copie modelos do tenant Normex para o novo tenant. Todos vêm marcados por padrão e você pode desmarcar o que não deseja migrar."}
              </p>
            </div>
            {!isReadOnly ? (
              <label className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm">
                <input type="checkbox" checked={migrateTemplates} onChange={(e) => setMigrateTemplates(e.target.checked)} disabled={isBusy} />
                Migrar modelos agora
              </label>
            ) : null}
          </div>

          {isReadOnly ? (
            <div className="mt-6 rounded-xl border bg-muted/40 p-4 text-sm text-muted-foreground">
              Esta etapa está em modo consulta. Abaixo está o resultado real dos pré-cadastros executados no onboarding; os modelos Normex não são recarregados para nova seleção.
            </div>
          ) : !migrateTemplates ? (
            <div className="mt-6 rounded-xl border bg-muted/40 p-4 text-sm text-muted-foreground">
              Etapa pulada. O tenant será preparado sem migrar modelos Normex neste onboarding.
            </div>
          ) : templatesQuery.isLoading ? (
            <div className="mt-6 rounded-xl border p-4 text-sm text-muted-foreground">Carregando modelos do tenant Normex...</div>
          ) : templatesQuery.isError ? (
            <div className="mt-6 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">Não foi possível carregar os modelos Normex.</div>
          ) : (
            <div className="mt-6 space-y-4">
              {TEMPLATE_GROUPS.map((group) => (
                <TemplateSelector
                  key={group.key}
                  title={group.title}
                  description={group.description}
                  items={templates?.[group.dataKey] || []}
                  selectedIds={templateSelection[group.key]}
                  onToggle={(id, checked) => toggleTemplateItem(group.key, id, checked)}
                  onToggleAll={(checked) => setAllTemplateItems(group.key, templates?.[group.dataKey] || [], checked)}
                  disabled={isReadOnly || isBusy}
                />
              ))}
            </div>
          )}

          {isReadOnly || result ? (
            hasTemplateSummary ? (
              <ResultSummary title="Resultado dos pré-cadastros" summary={persistedTemplateSummary} />
            ) : (
              <div className="mt-6 rounded-xl border bg-muted/40 p-4 text-sm text-muted-foreground">Pré-cadastros Normex não foram registrados neste onboarding.</div>
            )
          ) : null}
        </Card>
      ) : null}

      {step === 4 ? (
        <Card className="p-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <h2 className="text-lg font-semibold">Importar estrutura e usuários</h2>
              <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
                {isReadOnly
                  ? "Consulte o resumo real da importação executada durante o onboarding."
                  : "Importe unidades, departamentos, cargos e usuários em uma única planilha. O telefone deve conter somente números com DDD, entre 10 e 11 dígitos."}
              </p>
            </div>
            {!isReadOnly ? (
              <label className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm">
                <input type="checkbox" checked={importNow} onChange={(e) => setImportNow(e.target.checked)} disabled={isBusy} />
                Importar agora
              </label>
            ) : null}
          </div>

          {isReadOnly ? (
            <div className="mt-6 rounded-xl border bg-muted/40 p-4 text-sm text-muted-foreground">
              Esta etapa está em modo consulta. A importação pelo onboarding não pode ser executada após a conclusão.
            </div>
          ) : !importNow ? (
            <div className="mt-6 rounded-xl border bg-muted/40 p-4 text-sm text-muted-foreground">
              Etapa pulada. O tenant será preparado sem importar usuários adicionais neste onboarding.
            </div>
          ) : (
            <div className="mt-6 grid gap-5 lg:grid-cols-[1fr_360px]">
              <div className="space-y-4">
                <div className="rounded-xl border p-4">
                  <div className="text-sm font-medium">Cabeçalho oficial obrigatório</div>
                  <div className="mt-3 grid gap-2 text-sm md:grid-cols-3">
                    {["nome", "email", "telefone", "unidade", "departamento", "cargo"].map((column, index) => (
                      <div key={column} className="rounded-lg bg-muted px-3 py-2 font-mono text-xs">
                        {index + 1}. {column}
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 overflow-x-auto rounded-lg border">
                    <table className="min-w-full text-left text-xs">
                      <thead className="bg-muted/60">
                        <tr>{["nome", "email", "telefone", "unidade", "departamento", "cargo"].map((h) => <th key={h} className="px-3 py-2 font-medium">{h}</th>)}</tr>
                      </thead>
                      <tbody>
                        <tr className="border-t"><td className="px-3 py-2">Maria Silva</td><td className="px-3 py-2">maria@empresa.com.br</td><td className="px-3 py-2">51999999999</td><td className="px-3 py-2">Matriz</td><td className="px-3 py-2">RH</td><td className="px-3 py-2">Analista de RH</td></tr>
                        <tr className="border-t"><td className="px-3 py-2">João Souza</td><td className="px-3 py-2">joao@empresa.com.br</td><td className="px-3 py-2">5199999999</td><td className="px-3 py-2">Filial Centro</td><td className="px-3 py-2">Financeiro</td><td className="px-3 py-2">Coordenador Financeiro</td></tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {importValidation ? (
                  <div className={importValidation.valid ? "rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" : "rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800"}>
                    <div className="font-medium">{importValidation.valid ? "Planilha validada" : "Planilha com erros"}</div>
                    <div className="mt-1">Total de registros: {importValidation.total_rows}</div>
                    {importValidation.errors.length ? (
                      <ul className="mt-3 list-disc space-y-1 pl-5">
                        {importValidation.errors.slice(0, 10).map((error) => <li key={error}>{error}</li>)}
                      </ul>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="space-y-3 rounded-xl border p-4">
                <FileSpreadsheet className="h-8 w-8 text-muted-foreground" />
                <div>
                  <div className="font-medium">Modelo Excel oficial</div>
                  <p className="mt-1 text-sm text-muted-foreground">Baixe o modelo, preserve os nomes das colunas e envie o arquivo preenchido.</p>
                </div>
                <Button variant="outline" onClick={handleDownloadTemplate} disabled={isBusy} className="w-full">
                  <Download className="mr-2 h-4 w-4" />
                  Baixar modelo
                </Button>
                <Input type="file" accept=".xlsx" onChange={(e) => handleImportFile(e.target.files?.[0])} disabled={isBusy} />
              </div>
            </div>
          )}

          {isReadOnly || result ? (
            <div className="mt-6 space-y-4">
              <Summary
                label="Importação"
                value={hasImportSummary ? (statusQuery.data?.organization_import_filename || organizationImport.filename || "Importação realizada") : "Importação pulada"}
              />
              {hasImportSummary ? (
                <ResultSummary title="Resultado da importação Excel" summary={persistedImportSummary} />
              ) : (
                <div className="rounded-xl border bg-muted/40 p-4 text-sm text-muted-foreground">Nenhuma importação foi registrada neste onboarding.</div>
              )}
            </div>
          ) : null}
        </Card>
      ) : null}

      {step === 5 ? (
        <Card className="p-6">
          <h2 className="text-lg font-semibold">Revisão</h2>
          <p className="mt-1 text-sm text-muted-foreground">Revise os dados antes de concluir a habilitação inicial.</p>

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            <Summary label="Tenant" value={tenantName} />
            <Summary label="Unidade/Filial inicial" value={unitName} />
            <Summary label="Departamento inicial" value={departmentName} />
            <Summary label="Cargo inicial" value={jobPositionName} />
            <Summary label="Grupo administrador" value={groupName} />
            <Summary label="Usuário administrador" value={`${adminName} · ${adminEmail}`} />
            <Summary label="Pré-cadastros Normex" value={isReadOnly ? (hasTemplateSummary ? "Realizados" : "Pulados") : (migrateTemplates ? `${selectedTemplateTotal(templateSelection)} item(ns) selecionado(s)` : "Pulados")} />
            <Summary label="Importação Excel" value={isReadOnly ? (hasImportSummary ? "Realizada" : "Pulada") : (importNow ? `${importValidation?.total_rows || 0} usuário(s) validado(s)` : "Pulada")} />
          </div>

          <div className="mt-5 rounded-xl border bg-muted/40 p-4 text-sm text-muted-foreground">
            Os pré-cadastros e a importação Excel serão gravados somente ao clicar em Concluir onboarding. Duplicidades serão ignoradas e exibidas no resumo final.
          </div>
        </Card>
      ) : null}

      {step === 6 ? (
        <Card className="p-6">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            <h2 className="text-lg font-semibold">Conclusão</h2>
          </div>

          <p className="mt-2 text-sm text-muted-foreground">
            {isConfigured
              ? "Este tenant já está com onboarding concluído. Consulte abaixo o resumo geral e reenvie o e-mail de boas-vindas se necessário."
              : "Revise o resumo final e clique em Concluir onboarding para habilitar o envio do e-mail de boas-vindas."}
          </p>

          <div className="mt-6 rounded-xl border bg-muted/30 p-4">
            <div className="text-sm font-medium">Resumo geral do onboarding</div>
            <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              <Summary label="Tenant" value={tenantName} />
              <Summary label="Status" value={isConfigured ? "Configurado" : currentStatus === "in_progress" ? "Em onboarding" : "Pendente"} />
              <Summary label="Unidade/Filial inicial" value={statusQuery.data?.unit_name || unitName || "-"} />
              <Summary label="Departamento inicial" value={statusQuery.data?.department_name || departmentName || "-"} />
              <Summary label="Cargo inicial" value={statusQuery.data?.job_position_name || jobPositionName || "-"} />
              <Summary label="Grupo administrador" value={statusQuery.data?.group_name || groupName || "-"} />
              <Summary label="Administrador" value={result?.admin_user_name || statusQuery.data?.admin_user_name || adminName || "-"} />
              <Summary label="E-mail do administrador" value={adminUserEmail || "-"} />
              <Summary label="Telefone do administrador" value={statusQuery.data?.admin_user_phone || adminPhone || "-"} />
              <Summary label="E-mail de boas-vindas" value={welcomeEmailStatusText} />
            </div>
          </div>

          <ResultSummary title="Resumo dos pré-cadastros" summary={persistedTemplateSummary} />
          <ResultSummary title="Resumo da importação Excel" summary={persistedImportSummary} />

          {onboardingCompletedAt ? (
            <div className="mt-6 rounded-xl border bg-muted/40 p-4 text-sm text-muted-foreground">
              Onboarding concluído em {onboardingCompletedAt}. Use a navegação acima para consultar cada etapa configurada.
            </div>
          ) : null}

          <div className="mt-6 rounded-xl border border-indigo-200 bg-indigo-50 p-4 text-sm text-indigo-950 dark:border-indigo-900 dark:bg-indigo-950/30 dark:text-indigo-100">
            <div className="font-medium">E-mail de boas-vindas do administrador</div>
            <p className="mt-1 text-indigo-800 dark:text-indigo-200">
              {!canSendWelcomeEmail
                ? "O e-mail de boas-vindas ficará disponível somente após concluir o onboarding."
                : welcomeAlreadySent
                  ? `Já foi enviado para ${adminUserEmail || "o administrador cadastrado"}. Você pode reenviar se o e-mail foi corrigido ou se o cliente não recebeu.`
                  : `Ainda não foi enviado para ${adminUserEmail || "o administrador cadastrado"}.`}
            </p>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <Button variant={welcomeAlreadySent ? "default" : "outline"} onClick={sendWelcomeEmail} disabled={isBusy || !adminUserId || !canSendWelcomeEmail}>
              <Mail className="mr-2 h-4 w-4" />
              {welcomeEmailActionLabel}
            </Button>
          </div>
        </Card>
      ) : null}

      <div className="mt-6 flex justify-between">
        <Button variant="outline" onClick={prevStep} disabled={step === 0 || isBusy}>
          Voltar etapa
        </Button>

        {step < 6 ? (
          <Button onClick={nextStep} disabled={(!isReadOnly && !canGoNext) || isBusy}>
            {isReadOnly ? "Próxima etapa" : "Avançar"}
          </Button>
        ) : !isConfigured ? (
          <Button onClick={requestFinish} disabled={isBusy || !canEditOnboarding}>
            Concluir onboarding
          </Button>
        ) : null}
      </div>

      <ConfirmDialog
        open={finishConfirmOpen}
        onOpenChange={setFinishConfirmOpen}
        title="Concluir onboarding"
        description="Ao concluir o onboarding, este tenant será marcado como configurado. As etapas de pré-cadastros e importação ficarão somente para consulta e não poderão mais ser executadas por esta tela. Deseja continuar?"
        confirmLabel="Sim, concluir onboarding"
        variant="default"
        onConfirm={handleFinish}
      />
    </div>
  );
}

function OnboardingBreadcrumb({ step, onSelect, disabled }: { step: number; onSelect: (step: number) => void; disabled?: boolean }) {
  return (
    <div className="w-full overflow-x-auto xl:w-auto">
      <nav className="flex min-w-max items-center rounded-lg border bg-background shadow-sm">
        {STEPS.map((item, index) => {
          const active = index === step;
          return (
            <div key={item.shortLabel} className="flex items-center">
              <button
                type="button"
                title={item.fullLabel}
                onClick={() => onSelect(index)}
                disabled={disabled}
                className={`whitespace-nowrap px-3 py-2 text-sm font-medium transition ${
                  active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted hover:text-foreground"
                } ${index === 0 ? "rounded-l-lg" : ""} ${index === STEPS.length - 1 ? "rounded-r-lg" : ""}`}
              >
                {item.shortLabel}
              </button>
              {index < STEPS.length - 1 ? <ChevronRight className="h-4 w-4 text-muted-foreground/60" /> : null}
            </div>
          );
        })}
      </nav>
    </div>
  );
}

function selectedTemplateTotal(selection: TemplateSelection) {
  return Object.values(selection).reduce((total, values) => total + values.length, 0);
}

function InputField({ label, value, onChange, disabled }: { label: string; value: string; onChange: (value: string) => void; disabled?: boolean }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled} />
    </div>
  );
}

function TemplateSelector({
  title,
  description,
  items,
  selectedIds,
  onToggle,
  onToggleAll,
  disabled,
}: {
  title: string;
  description: string;
  items: TenantOnboardingTemplateItem[];
  selectedIds: string[];
  onToggle: (id: string, checked: boolean) => void;
  onToggleAll: (checked: boolean) => void;
  disabled?: boolean;
}) {
  const allSelected = items.length > 0 && selectedIds.length === items.length;
  return (
    <div className="rounded-xl border p-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="font-medium">{title}</div>
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={allSelected} onChange={(e) => onToggleAll(e.target.checked)} disabled={disabled || items.length === 0} />
          Selecionar todos ({selectedIds.length}/{items.length})
        </label>
      </div>

      {items.length ? (
        <div className="mt-4 grid max-h-56 gap-2 overflow-y-auto pr-2 md:grid-cols-2">
          {items.map((item) => (
            <label key={item.id} className="flex items-start gap-3 rounded-lg border bg-background px-3 py-2 text-sm">
              <input type="checkbox" checked={selectedIds.includes(item.id)} onChange={(e) => onToggle(item.id, e.target.checked)} disabled={disabled} className="mt-1" />
              <div>
                <div className="font-medium leading-5">{item.label}</div>
                {item.meta ? <div className="mt-0.5 text-xs text-muted-foreground">{item.meta}</div> : null}
                {item.description ? <div className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{item.description}</div> : null}
              </div>
            </label>
          ))}
        </div>
      ) : (
        <div className="mt-4 rounded-lg bg-muted/50 px-3 py-2 text-sm text-muted-foreground">Nenhum item disponível no tenant Normex.</div>
      )}
    </div>
  );
}

function Summary({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="rounded-xl border p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={strong ? "mt-1 font-mono text-base font-bold" : "mt-1 text-sm font-medium"}>{value || "-"}</div>
    </div>
  );
}

function ResultSummary({ title, summary }: { title: string; summary?: Record<string, TenantOnboardingSummarySection> }) {
  const entries = Object.entries(summary || {}).filter(([, value]) => value.created || value.skipped_existing || value.skipped_dependency || value.errors?.length);
  if (!entries.length) return null;

  return (
    <div className="mt-6 rounded-xl border p-4">
      <div className="text-sm font-medium">{title}</div>
      <div className="mt-3 grid gap-3 md:grid-cols-2">
        {entries.map(([key, value]) => (
          <div key={key} className="rounded-lg bg-muted/40 p-3 text-sm">
            <div className="font-medium">{summaryLabel(key)}</div>
            <div className="mt-1 text-xs text-muted-foreground">
              Criados: {value.created} · Existentes ignorados: {value.skipped_existing} · Dependências ignoradas: {value.skipped_dependency}
            </div>
            {value.errors?.length ? (
              <ul className="mt-2 list-disc space-y-1 pl-4 text-xs text-muted-foreground">
                {value.errors.slice(0, 4).map((error) => <li key={error}>{error}</li>)}
              </ul>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function summaryLabel(key: string) {
  const labels: Record<string, string> = {
    risk_matrices: "Matrizes de risco",
    risk_matrix_probability_levels: "Níveis de probabilidade",
    risk_matrix_severity_levels: "Níveis de severidade",
    risk_matrix_cells: "Células da matriz",
    risk_sources: "Fontes de risco",
    dimensions: "Dimensões",
    risks: "Riscos",
    action_plan_models: "Planos de ação",
    groups: "Grupos de acesso",
    dimension_risk_rules: "Vínculo Riscos × Dimensões",
    risk_action_plan_models: "Vínculo Planos × Riscos",
    questionnaires: "Questionários",
    units: "Unidades",
    departments: "Departamentos",
    job_positions: "Cargos",
    users: "Usuários",
  };
  return labels[key] || key;
}
